<script setup lang="ts">
import { useCourseExemptionMutations } from '@/composables/mutations/tertiary/useCourseExemptionMutations';
import { useStudents } from '@/composables/queries/useStudent';
import { useCourses } from '@/composables/queries/useCourses';
import { formatCourseInstance } from '@/utils/courseInstance';
import { EnrollmentStatusEnum } from '@/types/composition-functions';

const emit = defineEmits<{ success: [] }>();
const visible = defineModel<boolean>('visible', { default: false });

const { bulkExemptByStudents, bulkExemptingByStudents } = useCourseExemptionMutations();

// Student search — exemptions only meaningful for active enrollments
const studentFilters = ref({
  first: 50,
  page: 1,
  filter: { search: '', status: EnrollmentStatusEnum.Enrolled },
});
const { students, loadStudents } = useStudents({
  getStudentsOptions: { autoFetch: false },
  studentFilters: studentFilters as never,
});

let studentDebounce: ReturnType<typeof setTimeout> | null = null;
function onStudentFilter(event: { value: string }) {
  if (studentDebounce) clearTimeout(studentDebounce);
  studentDebounce = setTimeout(() => {
    studentFilters.value = {
      first: 50,
      page: 1,
      filter: { search: event.value, status: EnrollmentStatusEnum.Enrolled },
    };
    loadStudents();
  }, 300);
}

const studentOptions = computed(() =>
  students.data.value.map((s) => ({
    label: `${s.matric_no} - ${s.surname} ${s.first_name}`,
    value: s.id,
  })),
);

// Courses — load initial set, then search server-side on filter
const { courses, setFilter: setCourseFilter } = useCourses({ autoFetch: true, first: 100, page: 1 });
const courseOptions = computed(() =>
  courses.value.map((c: { id: string; course_code: string; course_title: string; unit: number; course_instance_id?: string; course_instance_year?: number | null }) => ({
    label: `${c.course_code} — ${c.course_title} (${c.unit} units)`,
    value: c.id,
    code: c.course_code,
    title: c.course_title,
    unit: c.unit,
    instance: formatCourseInstance(c),
  })),
);

let courseDebounce: ReturnType<typeof setTimeout> | null = null;
function onCourseFilter(event: { originalEvent: Event; value: string }) {
  if (courseDebounce) clearTimeout(courseDebounce);
  courseDebounce = setTimeout(() => {
    setCourseFilter({ query: event.value });
  }, 300);
}

// Form state
const selectedStudents = ref<string[]>([]);
const selectedCourses = ref<string[]>([]);

async function handleSubmit() {
  if (!selectedStudents.value.length || !selectedCourses.value.length) return;
  const result = await bulkExemptByStudents({
    student_ids: selectedStudents.value,
    course_ids: selectedCourses.value,
  });
  if (result?.success) {
    selectedStudents.value = [];
    selectedCourses.value = [];
    visible.value = false;
    emit('success');
  }
}

function onHide() {
  selectedStudents.value = [];
  selectedCourses.value = [];
}
</script>

<template>
  <Dialog data-testid="bulk-students-dialog" v-model:visible="visible" header="Bulk Exempt by Students" modal :style="{ width: '36rem' }" @hide="onHide">
    <div class="flex flex-col gap-4 pt-2">
      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700">Students</label>
        <MultiSelect
          data-testid="bulk-students-student-select"
          v-model="selectedStudents"
          :options="studentOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Search and select students..."
          filter
          @filter="onStudentFilter"
          :maxSelectedLabels="3"
          class="w-full"
        />
        <small class="text-gray-500">{{ selectedStudents.length }} student(s) selected</small>
      </div>

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-gray-700">Courses to Exempt</label>
        <MultiSelect
          data-testid="bulk-students-course-select"
          v-model="selectedCourses"
          :options="courseOptions"
          optionLabel="label"
          optionValue="value"
          placeholder="Search courses..."
          filter
          @filter="onCourseFilter"
          :maxSelectedLabels="3"
          class="w-full"
        >
          <template #option="{ option }">
            <div class="flex flex-col py-0.5">
              <span class="text-sm font-medium">{{ option.code }} — {{ option.title }}</span>
              <span class="text-xs text-gray-400">Unit: {{ option.unit ?? '—' }} · {{ option.instance || '—' }}</span>
            </div>
          </template>
        </MultiSelect>
        <small class="text-gray-500">{{ selectedCourses.length }} course(s) selected</small>
      </div>
    </div>

    <template #footer>
      <Button size="small" data-testid="bulk-students-cancel" label="Cancel" severity="secondary" text @click="visible = false" />
      <Button size="small"
        data-testid="bulk-students-submit"
        label="Apply Exemptions"
        icon="pi pi-check"
        @click="handleSubmit"
        :loading="bulkExemptingByStudents"
        :disabled="!selectedStudents.length || !selectedCourses.length"
      />
    </template>
  </Dialog>
</template>
